/**
 * CRM Leads — Phase 2: Internal Lead Foundation (list).
 *
 * Operator queue for the QDrives WhatsApp Vehicle Acquisition CRM.
 * Lists leads stored in `crm_leads`. Pure CRUD — no WhatsApp connection
 * in this phase. Mounts at `/admin/crm`.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, X as XIcon, ChatCircle } from "@phosphor-icons/react";
import WaCloudSafetyBanner from "@/components/WaCloudSafetyBanner";
import PhoneQuickActions from "@/components/PhoneQuickActions";

const STATUS_TONE = {
  new_whatsapp_lead:              "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  waiting_for_form:               "bg-amber-500/15 text-amber-400 border-amber-500/30",
  manual_intake_required:         "bg-orange-500/15 text-orange-400 border-orange-500/30",
  details_collection_in_progress: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  details_collected:              "bg-sky-500/15 text-sky-300 border-sky-500/30",
  form_submitted:                 "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  evaluation_pending:             "bg-violet-500/15 text-violet-300 border-violet-500/30",
  inspection_pending:             "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  offer_given:                    "bg-pink-500/15 text-pink-300 border-pink-500/30",
  negotiation:                    "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  purchased:                      "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  lost:                           "bg-red-500/15 text-red-400 border-red-500/30",
  not_interested:                 "bg-white/5 text-white/40 border-white/15",
};

const SOURCE_LABEL = {
  whatsapp: "WhatsApp",
  website:  "Website Form",
  manual:   "Manual",
};

export default function CrmLeads() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [statusOpts, setStatusOpts] = useState([]);
  const [source, setSource] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  // Phase 8.1 — attribution filters & summary
  const [attrSource, setAttrSource] = useState("");
  const [attrCampaign, setAttrCampaign] = useState("");
  const [attrMissing, setAttrMissing] = useState(false);
  // Phase 10.6 — Needs-Attention filter (customer replied while Waiting for Form).
  const [needsAttentionOnly, setNeedsAttentionOnly] = useState(false);
  // Phase 10.6b — honor `?filter=needs_attention` URL param (used by the toast
  // shortcut in useNeedsAttention). Applies the filter once on mount if present.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("filter") === "needs_attention") {
      setNeedsAttentionOnly(true);
      // clean the param so back-nav doesn't re-apply
      const next = new URLSearchParams(searchParams);
      next.delete("filter");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [attrSummary, setAttrSummary] = useState(null);
  // Phase 10.5 — status breakdown (all enquiry statuses shown as clickable cards).
  const [statusBreakdown, setStatusBreakdown] = useState(null);

  const fetchStatusBreakdown = async () => {
    try {
      const params = {};
      if (q.trim())           params.q = q.trim();
      if (source)             params.source = source;
      if (attrSource)         params.attr_source = attrSource;
      if (attrCampaign.trim()) params.attr_campaign = attrCampaign.trim();
      if (attrMissing)        params.attr_missing = true;
      if (needsAttentionOnly) params.needs_attention = true;
      const { data } = await api.get("/admin/crm/status-breakdown", { params });
      setStatusBreakdown(data);
    } catch {
      // silent — breakdown is enhancement-only
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      if (source) params.source = source;
      if (attrSource) params.attr_source = attrSource;
      if (attrCampaign.trim()) params.attr_campaign = attrCampaign.trim();
      if (attrMissing) params.attr_missing = true;
      if (needsAttentionOnly) params.needs_attention = true;
      params.limit = 100;
      const { data } = await api.get("/admin/crm/leads", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/admin/crm/statuses")
      .then(({ data }) => setStatusOpts(data.statuses || []))
      .catch(() => {});
    api.get("/admin/crm/attribution/summary", { params: { days: 30 } })
      .then(({ data }) => setAttrSummary(data))
      .catch(() => {});
  }, []);

  // Debounced search re-fetch
  useEffect(() => {
    const t = setTimeout(fetchData, q.trim() || attrCampaign.trim() ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, source, attrSource, attrCampaign, attrMissing]);

  // Phase 10.5 — refresh status breakdown whenever non-status filters change.
  useEffect(() => {
    const t = setTimeout(fetchStatusBreakdown, q.trim() || attrCampaign.trim() ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, source, attrSource, attrCampaign, attrMissing]);

  const onCreated = (lead) => {
    setShowCreate(false);
    if (lead?.id) nav(`/admin/crm/${lead.id}`);
  };

  return (
    <div className="space-y-4" data-testid="crm-leads-page">
      <WaCloudSafetyBanner />

      {/* Phase 10.6b — Needs-Attention prominent banner (only when count > 0
          and the filter isn't already applied). Clicking "View Now" applies
          the filter without a navigation. */}
      {(statusBreakdown?.needs_attention || 0) > 0 && !needsAttentionOnly && (
        <div
          data-testid="crm-needs-attention-banner"
          className="border border-amber-500 bg-gradient-to-r from-amber-500/20 via-amber-500/10 to-transparent px-4 py-3 flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-2xl leading-none">⚡</span>
            <div>
              <div className="text-[12px] font-bold tracking-widest uppercase text-amber-200">
                {statusBreakdown.needs_attention} WhatsApp{" "}
                {statusBreakdown.needs_attention === 1 ? "reply needs" : "replies need"}{" "}
                attention
              </div>
              <div className="text-[10px] text-white/60 mt-0.5">
                Customers replied to the form link but did not submit. Review and start Manual Intake.
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid="crm-needs-attention-banner-view"
            onClick={() => setNeedsAttentionOnly(true)}
            className="shrink-0 text-[10px] tracking-widest uppercase font-bold py-2 px-3 border border-amber-400 bg-amber-500 text-black hover:bg-amber-400"
          >
            View Now
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] text-white/40 tracking-[0.2em] uppercase font-display">
            QDrives WhatsApp Vehicle Acquisition CRM · Phase 2
          </div>
          <h1 className="text-white text-xl sm:text-2xl font-semibold tracking-tight">
            CRM Leads <span className="text-white/40 text-sm font-mono">({total})</span>
          </h1>
        </div>
        <button
          data-testid="crm-create-lead-btn"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-3 py-2 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-[#FF1E1E] hover:bg-[#E61919] transition-colors"
        >
          <Plus size={14} weight="bold" /> New Lead
        </button>
      </div>

      {/* Phase 8.1/8.2 — Attribution summary (last 30 days) */}
      {attrSummary && (
        <div className="space-y-2" data-testid="crm-attribution-summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <SummaryPill label="Leads (30d)" value={attrSummary.total} testid="attr-total" />
            <SummaryPill label="Missing Attr" value={attrSummary.missing_or_unknown} tone={attrSummary.missing_or_unknown > 0 ? "amber" : "ok"} testid="attr-missing" />
            <SummaryPill label="Top Source" value={attrSummary.by_source?.[0]?.key || "—"} sub={attrSummary.by_source?.[0]?.count} testid="attr-top-source" />
            <SummaryPill label="Top Campaign" value={attrSummary.by_campaign?.[0]?.key || "—"} sub={attrSummary.by_campaign?.[0]?.count} testid="attr-top-campaign" />
          </div>
          {/* Phase 8.2 — per-channel breakdown */}
          {attrSummary.counts && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="crm-attribution-channel-breakdown">
              <ChannelPill source="meta"     count={attrSummary.counts.meta} />
              <ChannelPill source="google"   count={attrSummary.counts.google} />
              <ChannelPill source="whatsapp" count={attrSummary.counts.whatsapp} />
              <ChannelPill source="direct"   count={attrSummary.counts.direct} />
              <ChannelPill source="organic"  count={attrSummary.counts.organic} />
              <ChannelPill source="manual"   count={attrSummary.counts.manual} />
            </div>
          )}
          {attrSummary.counts && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="crm-attribution-channel-totals">
              <SummaryPill label="Website Form" value={attrSummary.counts.website_form} testid="attr-website-form" />
              <SummaryPill label="WhatsApp Leads" value={attrSummary.counts.whatsapp_lead} testid="attr-whatsapp-leads" />
              <SummaryPill label="Missing / Unknown" value={attrSummary.counts.missing} tone={attrSummary.counts.missing > 0 ? "amber" : "ok"} testid="attr-missing-2" />
            </div>
          )}
        </div>
      )}

      {/* Phase 10.5 — Status breakdown (clickable cards). Reflects current non-status filters. */}
      {statusBreakdown && (
        <div className="space-y-2" data-testid="crm-status-breakdown">
          {/* Summary strip */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <SummaryPill label="Total Leads"       value={statusBreakdown.total}                 testid="sb-total" />
            <SummaryPill label="Website Form"      value={statusBreakdown.by_source?.website}    testid="sb-website" />
            <SummaryPill label="WhatsApp Leads"    value={statusBreakdown.by_source?.whatsapp}   testid="sb-whatsapp" />
            <SummaryPill label="Missing / Unknown"
                         value={(statusBreakdown.by_source?.unknown || 0) + (statusBreakdown.missing_or_unknown_status || 0)}
                         tone={((statusBreakdown.by_source?.unknown || 0) + (statusBreakdown.missing_or_unknown_status || 0)) > 0 ? "amber" : "ok"}
                         testid="sb-missing" />
            {/* Phase 10.6 — Needs Attention counter (clickable filter). */}
            <button
              type="button"
              onClick={() => setNeedsAttentionOnly(v => !v)}
              data-testid="sb-needs-attention-toggle"
              title={needsAttentionOnly ? "Click to show all leads" : "Click to show only leads that replied but did not submit the form"}
              className={`text-left border px-2.5 py-2 transition-colors ${
                needsAttentionOnly
                  ? "border-red-500 bg-red-500/10"
                  : (statusBreakdown.needs_attention || 0) > 0
                    ? "border-amber-500/60 bg-amber-500/10 hover:border-amber-400"
                    : "border-[#2A2A2A] bg-[#0D0D0D]/60 hover:border-white/30"
              }`}
            >
              <div className="text-[9px] tracking-widest uppercase font-display text-white/45">
                Needs Attention{needsAttentionOnly ? " ✓" : ""}
              </div>
              <div className={`text-lg font-bold font-mono ${
                (statusBreakdown.needs_attention || 0) > 0 ? "text-amber-200" : "text-white/35"
              }`}>
                {statusBreakdown.needs_attention || 0}
              </div>
            </button>
          </div>

          {/* Status breakdown header */}
          <div className="flex items-center justify-between pt-1">
            <div className="text-[10px] tracking-widest uppercase font-display text-white/50">
              Status Breakdown
            </div>
            {status && (
              <button
                type="button"
                data-testid="sb-clear-status"
                onClick={() => setStatus("")}
                className="text-[9px] tracking-widest uppercase font-bold text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-2 py-1"
              >
                ✕ Clear status filter
              </button>
            )}
          </div>

          {/* Status cards — horizontal scroll on mobile, grid on larger screens */}
          <div className="grid grid-flow-col auto-cols-[minmax(150px,1fr)] sm:grid-flow-row sm:auto-cols-auto sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 overflow-x-auto pb-1 -mx-1 px-1"
               data-testid="sb-status-grid">
            {(statusBreakdown.by_status || []).map(s => {
              const active = status === s.key;
              const tone = STATUS_TONE[s.key] || "bg-white/5 text-white/60 border-white/15";
              return (
                <button
                  key={s.key}
                  type="button"
                  data-testid={`sb-status-card-${s.key}`}
                  onClick={() => setStatus(active ? "" : s.key)}
                  title={active ? "Click to clear this status filter" : `Click to filter by ${s.label}`}
                  className={`text-left border px-2.5 py-2 transition-colors ${
                    active
                      ? "border-[#FF1E1E] bg-[#FF1E1E]/10"
                      : "border-[#2A2A2A] bg-[#0D0D0D]/60 hover:border-white/30"
                  }`}
                >
                  <div className={`text-[8.5px] tracking-widest uppercase font-display truncate ${active ? "text-[#FF6F6F]" : "text-white/45"}`}>
                    {s.label}
                  </div>
                  <div className="flex items-baseline gap-2 mt-0.5">
                    <span className={`text-base font-bold font-mono ${s.count > 0 ? "text-white" : "text-white/35"}`}>
                      {s.count}
                    </span>
                    {s.count > 0 && (
                      <span className={`inline-block text-[8px] font-display tracking-wider uppercase px-1 border ${tone}`}>
                        {s.count === 1 ? "lead" : "leads"}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
            {statusBreakdown.missing_or_unknown_status > 0 && (
              <button
                type="button"
                data-testid="sb-status-card-missing"
                disabled
                className="text-left border border-amber-500/40 bg-amber-500/5 px-2.5 py-2 cursor-help"
                title="Leads whose `status` field is null, empty, or not in the recognised list."
              >
                <div className="text-[8.5px] tracking-widest uppercase font-display text-amber-300/80 truncate">
                  Missing / Unknown
                </div>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-base font-bold font-mono text-amber-200">
                    {statusBreakdown.missing_or_unknown_status}
                  </span>
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="crm-leads-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search phone or name…"
            className="qd-input-block w-full pl-9 py-2 text-sm"
          />
          {q && (
            <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white" aria-label="Clear">
              <XIcon size={14} />
            </button>
          )}
        </div>
        <select
          data-testid="crm-leads-filter-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="qd-input-block py-2 text-sm min-w-[180px]"
        >
          <option value="">All Statuses</option>
          {statusOpts.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
        <select
          data-testid="crm-leads-filter-source"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="qd-input-block py-2 text-sm min-w-[140px]"
        >
          <option value="">All Sources</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="website">Website</option>
          <option value="manual">Manual</option>
        </select>

        {/* Phase 8.1 — Attribution filters */}
        <select
          data-testid="crm-leads-filter-attr-source"
          value={attrSource}
          onChange={(e) => setAttrSource(e.target.value)}
          className="qd-input-block py-2 text-sm min-w-[140px]"
          title="Filter by attribution source"
        >
          <option value="">All Attribution</option>
          <option value="meta">Meta</option>
          <option value="google">Google</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="organic">Organic</option>
          <option value="direct">Direct</option>
          <option value="unknown">Unknown</option>
        </select>
        <input
          data-testid="crm-leads-filter-attr-campaign"
          value={attrCampaign}
          onChange={(e) => setAttrCampaign(e.target.value)}
          placeholder="Campaign name…"
          className="qd-input-block py-2 text-sm min-w-[140px]"
        />
        <label className="flex items-center gap-1 text-[10px] tracking-widest uppercase font-display text-white/55 px-2">
          <input
            data-testid="crm-leads-filter-attr-missing"
            type="checkbox"
            checked={attrMissing}
            onChange={(e) => setAttrMissing(e.target.checked)}
          />
          Missing Attr
        </label>
      </div>

      {/* List */}
      <div className="border border-white/10 bg-[#0E0E0E]/60">
        {loading ? (
          <div className="p-8 text-center text-white/40 text-sm" data-testid="crm-leads-loading">Loading leads…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-white/40 text-sm" data-testid="crm-leads-empty">
            No leads found. <button onClick={() => setShowCreate(true)} className="text-[#FF1E1E] underline">Create your first lead</button>.
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-white/5 text-white/45 text-[10px] tracking-widest uppercase font-display">
                <tr>
                  <th className="text-left px-3 py-2.5">Name / Phone</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-left px-3 py-2.5">Source</th>
                  <th className="text-left px-3 py-2.5">Attribution</th>
                  <th className="text-left px-3 py-2.5">Assigned</th>
                  <th className="text-left px-3 py-2.5">Follow-up</th>
                  <th className="text-left px-3 py-2.5">Form</th>
                  <th className="text-left px-3 py-2.5">Vehicles</th>
                  <th className="text-left px-3 py-2.5">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {items.map((l) => {
                  const vehicleCount = Array.isArray(l.intake_paths) ? l.intake_paths.length : 0;
                  return (
                  <tr
                    key={l.id}
                    data-testid={`crm-lead-row-${l.id}`}
                    onClick={() => nav(`/admin/crm/${l.id}`)}
                    className="hover:bg-white/5 cursor-pointer transition-colors"
                  >
                    <td className="px-3 py-2.5">
                      <div className="text-white font-medium">{l.name || <span className="text-white/40 italic">Unnamed</span>}</div>
                      <div className="text-white/45 text-xs font-mono">{l.phone}</div>
                      {l.phone && (
                        <div className="mt-1.5">
                          <PhoneQuickActions phone={l.phone} compact testidPrefix={`crm-list-phoneqa-${l.id}`} />
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-block text-[10px] font-display tracking-wider uppercase px-2 py-0.5 border w-fit ${STATUS_TONE[l.status] || "bg-white/5 text-white/40 border-white/15"}`}>
                          {l.status_label}
                        </span>
                        {l.needs_attention && (
                          <span
                            data-testid={`crm-needs-attention-${l.id}`}
                            title={`Customer replied but form not submitted — ${l.attention_reason || "review needed"}`}
                            className="inline-block text-[9px] font-display tracking-wider uppercase px-1.5 py-0.5 border border-amber-500 bg-amber-500/15 text-amber-300 w-fit animate-pulse"
                          >
                            ⚡ Customer Replied
                          </span>
                        )}
                        {l.needs_attention && l.last_customer_reply_preview && (
                          <span className="text-[10px] text-white/50 italic truncate max-w-[220px]"
                                title={l.last_customer_reply_preview}>
                            &ldquo;{l.last_customer_reply_preview}&rdquo;
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-white/70 text-xs">{SOURCE_LABEL[l.source] || l.source}</td>
                    <td className="px-3 py-2.5 text-xs" data-testid={`crm-attr-cell-${l.id}`}>
                      <AttributionPill attr={l.attribution} />
                    </td>
                    <td className="px-3 py-2.5 text-white/70 text-xs">{l.assigned_to_name || <span className="text-white/30">—</span>}</td>
                    <td className="px-3 py-2.5 text-white/70 text-xs font-mono">{l.followup_date || <span className="text-white/30">—</span>}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {l.form_submitted
                        ? <span className="text-emerald-400">✓ Yes</span>
                        : <span className="text-white/30">No</span>}
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      {vehicleCount > 0
                        ? <span
                            data-testid={`crm-vehicle-count-${l.id}`}
                            title={`${vehicleCount} website vehicle submission${vehicleCount === 1 ? "" : "s"} from this phone`}
                            className={`inline-block text-[10px] font-display tracking-wider uppercase px-1.5 py-0.5 border ${
                              vehicleCount > 1
                                ? "bg-[#FF1E1E]/10 border-[#FF1E1E]/40 text-[#FF6F6F]"
                                : "bg-white/5 border-white/15 text-white/55"
                            }`}
                          >
                            {vehicleCount} Vehicle{vehicleCount === 1 ? "" : "s"}
                          </span>
                        : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-white/45 text-xs font-mono">{(l.created_at || "").slice(0, 10)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile stacked cards */}
            <div className="md:hidden divide-y divide-white/5">
              {items.map((l) => {
                const vehicleCount = Array.isArray(l.intake_paths) ? l.intake_paths.length : 0;
                return (
                <Link
                  key={l.id}
                  to={`/admin/crm/${l.id}`}
                  data-testid={`crm-lead-card-${l.id}`}
                  className="block p-3 active:bg-white/5"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="min-w-0">
                      <div className="text-white font-medium truncate">{l.name || "Unnamed"}</div>
                      <div className="text-white/45 text-xs font-mono">{l.phone}</div>
                      {l.phone && (
                        <div className="mt-1.5">
                          <PhoneQuickActions phone={l.phone} compact testidPrefix={`crm-card-phoneqa-${l.id}`} />
                        </div>
                      )}
                    </div>
                    <span className={`shrink-0 text-[9px] font-display tracking-wider uppercase px-1.5 py-0.5 border ${STATUS_TONE[l.status] || "bg-white/5 text-white/40 border-white/15"}`}>
                      {l.status_label}
                    </span>
                  </div>
                  {l.needs_attention && (
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span
                        data-testid={`crm-card-needs-attention-${l.id}`}
                        className="inline-block text-[9px] font-display tracking-wider uppercase px-1.5 py-0.5 border border-amber-500 bg-amber-500/15 text-amber-300 animate-pulse">
                        ⚡ Customer Replied · {l.attention_reason || "review"}
                      </span>
                      {l.last_customer_reply_preview && (
                        <span className="text-[10px] text-white/50 italic truncate flex-1"
                              title={l.last_customer_reply_preview}>
                          &ldquo;{l.last_customer_reply_preview}&rdquo;
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-white/55 mt-1">
                    <span>{SOURCE_LABEL[l.source] || l.source}</span>
                    {l.assigned_to_name && <span>· {l.assigned_to_name}</span>}
                    {l.followup_date && <span className="font-mono">· FU {l.followup_date}</span>}
                    {l.form_submitted && <span className="text-emerald-400">· ✓ Form</span>}
                    {vehicleCount > 0 && (
                      <span
                        data-testid={`crm-vehicle-count-mobile-${l.id}`}
                        className={vehicleCount > 1 ? "text-[#FF6F6F]" : "text-white/55"}
                      >· {vehicleCount} Vehicle{vehicleCount === 1 ? "" : "s"}</span>
                    )}
                  </div>
                </Link>
                );
              })}
            </div>
          </>
        )}
      </div>

      {showCreate && <CreateLeadModal statusOpts={statusOpts} onClose={() => setShowCreate(false)} onCreated={onCreated} />}
    </div>
  );
}

function CreateLeadModal({ statusOpts, onClose, onCreated }) {
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [source, setSource] = useState("manual");
  const [status, setStatus] = useState("new_whatsapp_lead");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!phone.trim()) { toast.error("Mobile number required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/crm/leads", {
        phone: phone.trim(),
        name: name.trim(),
        source,
        status,
        initial_note: note.trim() || undefined,
      });
      toast.success("Lead created");
      onCreated?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.lead_id) {
        toast.info("Lead with this number already exists — opening it");
        nav(`/admin/crm/${d.lead_id}`);
        onClose?.();
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Create failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="crm-create-lead-modal" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-[#111] border border-white/10 p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-white text-base font-semibold">New CRM Lead</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white"><XIcon size={18} /></button>
        </div>
        <div>
          <label className="text-[10px] text-white/55 tracking-widest uppercase font-display">Mobile Number *</label>
          <input data-testid="crm-create-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 9XXXX XXXXX" className="qd-input-block w-full py-2 text-sm mt-1" />
          <div className="text-[10px] text-white/35 mt-1">10-digit Indian or full E.164 (+91…)</div>
        </div>
        <div>
          <label className="text-[10px] text-white/55 tracking-widest uppercase font-display">Name</label>
          <input data-testid="crm-create-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name" className="qd-input-block w-full py-2 text-sm mt-1" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-white/55 tracking-widest uppercase font-display">Source</label>
            <select data-testid="crm-create-source" value={source} onChange={(e) => setSource(e.target.value)} className="qd-input-block w-full py-2 text-sm mt-1">
              <option value="manual">Manual</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="website">Website</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-white/55 tracking-widest uppercase font-display">Status</label>
            <select data-testid="crm-create-status" value={status} onChange={(e) => setStatus(e.target.value)} className="qd-input-block w-full py-2 text-sm mt-1">
              {statusOpts.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-[10px] text-white/55 tracking-widest uppercase font-display">Initial Note (optional)</label>
          <textarea data-testid="crm-create-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="How you reached this lead…" className="qd-input-block w-full py-2 text-sm mt-1" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-2 text-[11px] tracking-widest uppercase font-display text-white/55 hover:text-white">Cancel</button>
          <button data-testid="crm-create-submit" onClick={submit} disabled={busy} className="px-4 py-2 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-[#FF1E1E] hover:bg-[#E61919] disabled:opacity-50">
            {busy ? "Creating…" : "Create Lead"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 8.1 — Attribution helpers
// ---------------------------------------------------------------------------
const ATTR_SOURCE_TONE = {
  meta:     "bg-blue-500/15 border-blue-500/40 text-blue-200",
  google:   "bg-green-500/15 border-green-500/40 text-green-200",
  whatsapp: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
  organic:  "bg-amber-500/10 border-amber-500/30 text-amber-200",
  direct:   "bg-white/5  border-white/15 text-white/60",
  manual:   "bg-violet-500/15 border-violet-500/30 text-violet-200",
  unknown:  "bg-orange-500/10 border-orange-500/30 text-orange-200",
};

function AttributionPill({ attr }) {
  if (!attr || !attr.source) {
    return <span className="text-white/30 text-[10px] italic">No attribution</span>;
  }
  const tone = ATTR_SOURCE_TONE[attr.source] || ATTR_SOURCE_TONE.unknown;
  const adId = attr.ad_id || attr.meta_ad_id;
  return (
    <div className="space-y-0.5 leading-tight">
      <span className={`inline-block text-[9px] font-display tracking-widest uppercase px-1.5 py-0.5 border ${tone}`}>
        {attr.source}
      </span>
      {attr.campaign_name && (
        <div className="text-white/70 text-[10px] truncate max-w-[160px]" title={attr.campaign_name}>
          {attr.campaign_name}
        </div>
      )}
      {adId && (
        <div className="text-white/40 text-[10px] font-mono truncate max-w-[160px]" title={adId}>
          ad:{adId}
        </div>
      )}
    </div>
  );
}

function SummaryPill({ label, value, sub, tone = "ok", testid }) {
  const klass = tone === "amber"
    ? "border-amber-500/30 bg-amber-500/5 text-amber-200"
    : "border-white/10 bg-[#0E0E0E]/60 text-white/80";
  return (
    <div className={`px-3 py-2 border ${klass}`} data-testid={testid}>
      <div className="text-[9px] tracking-widest uppercase font-display text-white/45">{label}</div>
      <div className="text-white text-sm font-semibold truncate">
        {value}{sub != null && <span className="text-white/45 text-[10px] ml-1 font-mono">· {sub}</span>}
      </div>
    </div>
  );
}

function ChannelPill({ source, count }) {
  const tone = ATTR_SOURCE_TONE[source] || ATTR_SOURCE_TONE.unknown;
  return (
    <div className={`px-2.5 py-1.5 border ${tone}`} data-testid={`attr-channel-${source}`}>
      <div className="text-[8.5px] tracking-widest uppercase font-display opacity-80">{source}</div>
      <div className="text-base font-bold font-mono">{count ?? 0}</div>
    </div>
  );
}
